import { useTranslation } from 'react-i18next';
import { FieldLabel, PageHead, PageToolbar } from '@/components/editorial';
import { NAV_KEY } from '@/i18n/keys/nav';
import { useMsg } from '@/i18n/msg';
import { FilterChips } from '@/components/common/FilterChips';
import {
  EXPENSE_CATEGORIES,
  EXPENSE_CATEGORY_TOKENS,
  isFinancesFiltered,
  type CategoryScope,
  type ClaimScope,
  type FinancesFilters,
  type GrantScope,
} from '@/lib/bills-display';
import { FAMILY_GRANT_STATUSES, familyGrantStatusScopeLabel } from '@/lib/grants-display';

// ── Header ────────────────────────────────────────────────────────────────

interface FinancesHeaderProps {
  filters: FinancesFilters;
  onFiltersChange: (f: FinancesFilters) => void;
  search: string;
  onSearchChange: (q: string) => void;
  /** Open the receipt capture zone. */
  onAddReceipt: () => void;
  /** Open the details dialog for a hand-entered expense. */
  onAddExpense: () => void;
}

/**
 * Eyebrow → toolbar. No tab row, no headline, no number (§1.6, §2.1).
 *
 * Rebuilt 2026-09-21 (spec Part 3 item 1). It carried three underline tabs —
 * TRF Grants · Expenses & receipts · Bill.com (test) — and `?tab=` was the
 * page's store. All three are gone: the page is one grant-centered spine, so
 * there is nothing to switch between.
 *
 * The eyebrow reads **Grants**, not Finances. Victor: "Are we
 * managing/applying for grants? Is it a dashboard?" — "Finances" promised
 * household budgeting and delivered a grant list plus a reimbursement
 * ledger. A row is named for what the reader does there (§2.8 rule 2), and
 * the reader manages TRF grants. The `/bills` href and the persisted `bills`
 * menu key are unchanged: labels rename, keys never.
 *
 * ONE filter popover. It used to hold a different set per tab; with one list
 * every field narrows the same thing, so grant status and the receipt scopes
 * sit together.
 */
export function FinancesHeader({
  filters,
  onFiltersChange,
  search,
  onSearchChange,
  onAddReceipt,
  onAddExpense,
}: FinancesHeaderProps) {
  const { t } = useTranslation(['grants', 'nav']);
  return (
    <header className="space-y-4 pb-3.5 border-b border-border/60">
      {/* The eyebrow and the toolbar name are the NAV row's own key (#2109
          R3-B23, brief B7), so nav and eyebrow read "Apoyos económicos"
          together. */}
      <PageHead eyebrowKey={NAV_KEY.grants} />
      {/* + · search · filter. No print and no export — neither action exists
          on this page, and the toolbar does not invent tools (C3). */}
      <PageToolbar
        label={t(NAV_KEY.grants)}
        add={{
          items: [
            { label: t('header.addReceipt'), onSelect: onAddReceipt },
            { label: t('header.addExpense'), onSelect: onAddExpense },
          ],
        }}
        search={{
          value: search,
          onChange: onSearchChange,
          placeholder: t('header.searchPlaceholder'),
        }}
        filter={{
          active: isFinancesFiltered(filters),
          label: t('header.filterLabel'),
          panel: <FinancesFilterPanel filters={filters} onChange={onFiltersChange} />,
        }}
      />
    </header>
  );
}

// ── Filter panel (inside the popover) ─────────────────────────────────────

/**
 * One popover for one list: the grant statuses `GrantsPanel` distinguishes,
 * then the receipt scope (Needs attention · With TRF · Paid back), then the
 * four categories.
 *
 * Every label outside the per-group "All" is unique, and a test says so.
 * Merging two popovers into one made that a live hazard rather than a
 * stylistic preference — "Under review" arrived as both a grant status and a
 * receipt scope, two identical chips a few rows apart meaning different
 * things.
 */
export function FinancesFilterPanel({
  filters,
  onChange,
}: {
  filters: FinancesFilters;
  onChange: (f: FinancesFilters) => void;
}) {
  const { t } = useTranslation('grants');
  const resolve = useMsg();
  return (
    <>
      <div className="space-y-2">
        <FieldLabel>{t('filters.grants')}</FieldLabel>
        <FilterChips
          value={filters.grantScope}
          onChange={(v) => onChange({ ...filters, grantScope: v as GrantScope })}
          options={[
            { value: 'all', label: t('filters.all') },
            ...FAMILY_GRANT_STATUSES.map((s) => ({ value: s, label: familyGrantStatusScopeLabel(s, t) })),
          ]}
        />
      </div>
      <div className="space-y-2">
        <FieldLabel>{t('filters.receipts')}</FieldLabel>
        <FilterChips
          value={filters.claimScope}
          onChange={(v) => onChange({ ...filters, claimScope: v as ClaimScope })}
          options={[
            { value: 'all', label: t('filters.all') },
            { value: 'attention', label: t('filters.scope.attention') },
            // "With TRF", not "Under review" (2026-09-21). One popover now
            // holds grant statuses AND receipt scopes, and "Under review" is
            // already a grant status — two identical chips a few rows apart
            // meaning different things. "With TRF" is also the truer label:
            // this scope spans submitted, under review, approved and paying,
            // all of which mean TRF has it and the family need do nothing.
            { value: 'review', label: t('filters.scope.review') },
            { value: 'paid', label: t('filters.scope.paid') },
          ]}
        />
      </div>
      <div className="space-y-2">
        <FieldLabel>{t('filters.category')}</FieldLabel>
        <FilterChips
          value={filters.categoryScope}
          onChange={(v) => onChange({ ...filters, categoryScope: v as CategoryScope })}
          options={[
            { value: 'all', label: t('filters.all') },
            ...EXPENSE_CATEGORIES.map((c) => ({ value: c, label: resolve({ key: EXPENSE_CATEGORY_TOKENS[c].labelKey }) })),
          ]}
        />
      </div>
    </>
  );
}
